import { formatPhone } from '@hue/contracts';
import { Button } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, Phone, Sparkles } from 'lucide-react';
import { schedulingApi, type Appointment } from '../../lib/api.js';
import {
  dayLabel,
  minuteLabel,
  professionalLabel,
  roomLabel,
  type PlacedAppointment,
} from './calendar-model.js';

export function AppointmentClientContext({
  appointment,
  actions = false,
}: {
  appointment: Appointment;
  actions?: boolean;
}) {
  const query = useQuery({
    queryKey: ['company', 'scheduling', 'preview', appointment.id],
    queryFn: () => schedulingApi.getAppointmentPreview(appointment.id),
    retry: false,
  });
  const data = query.data;
  if (!data)
    return (
      <p className="text-xs text-content-muted">
        {query.error ? 'Contato e histórico indisponíveis.' : 'Consultando contato e histórico…'}
      </p>
    );
  const digits = data.phone?.replace(/\D/gu, '');
  return (
    <div className="grid gap-2">
      {data.phone ? (
        <p className="flex items-center gap-2 text-sm">
          <Phone className="size-4 shrink-0" />
          {formatPhone(data.phone)}
        </p>
      ) : (
        <p className="text-xs text-content-muted">Telefone não cadastrado.</p>
      )}
      {actions && digits ? (
        <Button asChild size="sm" variant="outline">
          <a href={`https://wa.me/${digits}`} target="_blank" rel="noreferrer">
            <MessageCircle className="size-4" /> WhatsApp
          </a>
        </Button>
      ) : null}
      {data.firstInCompany || data.firstInUnit || data.firstForService ? (
        <p className="flex items-start gap-2 rounded-lg bg-brand-500/10 px-2 py-1.5 text-xs text-brand-400">
          <Sparkles className="size-4 shrink-0" />
          <span>
            {data.firstInCompany ? (
              'Primeiro atendimento registrado na empresa.'
            ) : (
              <>
                {data.firstInUnit ? 'Primeiro atendimento registrado nesta unidade. ' : ''}
                {data.firstForService ? 'Primeira vez neste serviço no histórico acessível.' : ''}
              </>
            )}
          </span>
        </p>
      ) : null}
    </div>
  );
}

export function AppointmentPreview({ appointment }: { appointment: PlacedAppointment }) {
  return (
    <div className="grid gap-2 py-1">
      <strong className="text-sm">{appointment.clientDisplayName}</strong>
      <span>
        {dayLabel(appointment.date)} · {minuteLabel(appointment.start)}–
        {minuteLabel(appointment.end)}
      </span>
      <span>{appointment.serviceDisplayName}</span>
      <span className="text-content-muted">
        {professionalLabel(appointment)} · {roomLabel(appointment)}
      </span>
      <AppointmentClientContext appointment={appointment} />
      {appointment.overlapWarning ? (
        <span className="text-warning">{appointment.overlapWarning}</span>
      ) : null}
    </div>
  );
}
